import React, { useEffect, useState } from 'react';
import AgentMemoryPanel from './AgentMemoryPanel';
import BoardPage from './BoardPage';
import CloseRunPage from './CloseRunPage';
import closeIntelApi from './closeIntelApi';
import ForecastContextPage from './ForecastContextPage';
import MonitorPage from './MonitorPage';
import './closeIntel.css';

const TABS = [
  { code: 'CI_01', key: 'monitor', label: 'Monitor', tip: 'Scan GL cells and disposition the surprise queue', Component: MonitorPage },
  { code: 'CI_02', key: 'memory', label: 'Agent memory', tip: 'Review suppression rules and disposition history', Component: AgentMemoryPanel },
  { code: 'CI_03', key: 'expectations', label: 'Forecast context', tip: 'Review forecast baselines, company guardrails, and cited context', Component: ForecastContextPage },
  { code: 'CI_04', key: 'close', label: 'Close run', tip: 'Run variance bridges and narrate the close', Component: CloseRunPage },
  { code: 'CI_05', key: 'board', label: 'Board page', tip: 'Review the earnings pack screen by screen', Component: BoardPage },
];

const readTabParam = () => {
  if (typeof window === 'undefined') return 'monitor';
  const value = new URLSearchParams(window.location.search).get('tab');
  return TABS.some((t) => t.key === value) ? value : 'monitor';
};

export default function CloseIntelHome() {
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(readTabParam);
  const ActiveComponent = TABS.find((item) => item.key === tab)?.Component || MonitorPage;

  // Cross-tab navigation used by page drills (board → workbench, close run → board).
  const navigateTab = (key, params = {}) => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      // Page-scoped params reset on every tab switch.
      ['finding', 'period', 'run', 'screen', 'view', 'execItem', 'returnTo'].forEach((name) => url.searchParams.delete(name));
      url.searchParams.set('tab', key);
      Object.entries(params).forEach(([name, value]) => {
        if (value) url.searchParams.set(name, String(value));
      });
      window.history.replaceState({}, '', url);
    }
    setTab(key);
  };

  useEffect(() => {
    closeIntelApi
      .health()
      .then(setHealth)
      .catch((e) => setError(String(e.message || e)));
  }, []);

  const pillStyle = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: `1px solid ${active ? '#FFAC03' : 'var(--ace-line)'}`,
    borderRadius: 99,
    background: 'transparent',
    color: active ? '#FFAC03' : 'hsl(var(--foreground))',
    padding: '8px 12px',
    cursor: 'pointer',
    transition: 'border-color 0.16s ease, color 0.16s ease'
  });

  return (
    <div className="ace-page close-intel-module" style={{ padding: '24px 32px', maxWidth: 1320, margin: '0 auto' }}>
      <div className="ace-eyebrow" style={{ marginBottom: 4 }}>
        CI_00 <span aria-hidden>●</span> Close Intelligence
      </div>
      <h1 style={{ margin: '0 0 16px' }}>Continuous monitor &amp; variance narrator</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className="ace-pill ci-tip"
            data-tip={t.tip}
            onClick={() => navigateTab(t.key)}
            style={pillStyle(tab === t.key)}
          >
            <span className="ace-eyebrow" style={{ marginRight: 6 }}>{t.code}</span>
            {t.label}
          </button>
        ))}
      </div>

      <ActiveComponent navigateTab={navigateTab} />

      <div className="ace-panel" style={{ marginTop: 12, padding: '10px 12px', borderRadius: 14, fontSize: 12 }}>
        {error ? (
          <p style={{ margin: 0, color: 'var(--ace-danger, #e5241d)' }}>Health check: {error}</p>
        ) : health ? (
          <p style={{ margin: 0, opacity: 0.75 }}>
            <span style={{ color: '#FFAC03' }}>Live</span> tables:{' '}
            {Object.entries(health.tables || {})
              .map(([name, ok]) => `${name} ${ok ? 'ready' : 'missing'}`)
              .join(' · ')}
          </p>
        ) : (
          <p style={{ margin: 0, opacity: 0.6 }}>Checking module health...</p>
        )}
      </div>
    </div>
  );
}
